import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { GridActions, GridService, NotificationService } from '@core/services';
import { remToPx } from '@util/layout';

import { CarrierLookupResponseRow } from '#types/carrier';
import { Filters } from '#types/grid';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@carrier/constants';
import { CarrierService } from '@carrier/services';
import { ShipmentService } from '@shipment/services';

@Component({
  selector: 'z-carrier-lookup-dialog',
  templateUrl: './carrier-lookup-dialog.component.html',
  styleUrls: ['./carrier-lookup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierLookupDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  stateOrProvinceList$ = this.shipmentService.stateOrProvinceDropdownList$;

  form: FormGroup;

  carrier: string;
  selectedRow: CarrierLookupResponseRow;

  matchTypes: Record<string, string> = {};

  container: HTMLDivElement;
  containerParent: HTMLDivElement;
  containerFilters: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  lookupId = 'carrier-lookup-dialog';

  gridId = CONSTANTS.GRID_CARRIER_LOOKUP_DIALOG;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private store: Store,
    public carrierService: CarrierService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private gridService: GridService,
    private authService: AuthService,
    private shipmentService: ShipmentService,
    private ngZone: NgZone,
    private notificationService: NotificationService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.authService.authenticated$.pipe(takeUntil(this.destroy$)).subscribe((authenticated) => {
      if (!authenticated) {
        this.onCancelClick();
      }
    });

    this.form = new FormGroup({
      carrier: new FormControl(),
      name: new FormControl(),
      stateOrProvince: new FormControl(),
      scac: new FormControl(),
      smallPackageCarrier: new FormControl(false),
      activeStatus: new FormControl(true),
    });

    this.shipmentService.requestLists();

    this.gridService
      .selectedRow$<CarrierLookupResponseRow>(this.gridId)
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedRow) => {
        this.selectedRow = selectedRow.data;
        this.carrier = this.selectedRow.carrier;

        if (selectedRow.selectionType === 'double') {
          this.onSelectClick();
        }
      });

    // close dialog if no longer authenticated
    this.authService.authenticated$
      .pipe(
        filter((authenticated) => !authenticated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onCancelClick();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const header: HTMLDivElement = this.containerParent.querySelector('.p-dialog-header');
        const filters: HTMLDivElement = this.container.querySelector('.filters');
        const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
        const paginator: HTMLDivElement = this.container.querySelector('.p-paginator');

        let offset = 0;

        if (header) {
          offset += Math.floor(header.offsetHeight);
        }

        if (filters) {
          offset += Math.floor(filters.offsetHeight);
        }

        if (footer) {
          offset += Math.floor(footer.offsetHeight);
        }

        if (paginator) {
          offset += Math.floor(paginator.offsetHeight);
        }

        this.carrierService.carrierLookupDialogDimensions$.next({
          container: {
            width: Math.floor(this.containerParent.offsetWidth),
            height: Math.floor(this.containerParent.offsetHeight),
          },
          grid: {
            width: Math.floor(this.containerParent.offsetWidth) - remToPx('2rem'),
            height: Math.floor(this.containerParent.offsetHeight) - offset,
          },
        });
      });
    });

    try {
      this.container = document.querySelector(`.${this.lookupId}`);

      if (this.container) {
        this.containerParent = this.container.closest(`.lookup-dialog`);

        if (this.containerParent) {
          this.containerResizeObserver.observe(this.containerParent);
        }

        this.containerFilters = this.container.querySelector('.filters');

        if (this.containerFilters) {
          this.containerResizeObserver.observe(this.containerFilters);
        }
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.containerParent) {
        this.containerResizeObserver.unobserve(this.containerParent);
      }

      if (this.containerResizeObserver && this.containerFilters) {
        this.containerResizeObserver.unobserve(this.containerFilters);
      }
    } catch {}
  }

  onSelectClick() {
    if (!this.selectedRow || !this.selectedRow.carrier) {
      this.notificationService.error({
        message: this.translate('carrier.carrier_lookup_dialog.dialog.error.no_carrier'),
        progressBar: true,
      });

      return;
    }

    this.carrierService.carrierLookupSelectedCarrier$.next(this.selectedRow.carrier);
    this.carrierService.carrierLookupSelectedCarrierMultiShip$.next(this.selectedRow.multiship);

    this.ref.close({ carrier: this.carrier, selectedRow: this.selectedRow });
  }

  onCancelClick() {
    this.carrierService.carrierLookupSelectedCarrier$.next(null);
    this.carrierService.carrierLookupSelectedCarrierMultiShip$.next(null);

    this.ref.close(undefined);
  }

  onMatchTypeChange(field: string, matchType: string) {
    this.matchTypes[field] = matchType;
  }

  applyFilters() {
    const filters: Filters = {};

    if (this.form.get('carrier').value) {
      filters.carrier = { value: this.form.get('carrier').value, matchMode: this.matchTypes.carrier ?? 'equals' };
    }

    if (this.form.get('name').value) {
      filters.name = { value: this.form.get('name').value, matchMode: this.matchTypes.name ?? 'startsWith' };
    }

    if (this.form.get('stateOrProvince').value) {
      filters.stateOrProvince = { value: this.form.get('stateOrProvince').value };
    }

    if (this.form.get('scac').value) {
      filters.scac = { value: this.form.get('scac').value, matchMode: this.matchTypes.scac ?? 'equals' };
    }

    if (this.form.get('smallPackageCarrier').value !== null) {
      filters.smallPackageCarrier = { value: this.form.get('smallPackageCarrier').value ? 'Y' : 'N' };
    }

    if (this.form.get('activeStatus').value !== null) {
      filters.activeOnly = { value: this.form.get('activeStatus').value ? 'Y' : 'N' };
    }

    this.store.dispatch(GridActions.SetFilters({ id: this.gridId, filters }));
  }

  reset(): void {
    this.form.setValue({
      carrier: null,
      name: null,
      stateOrProvince: null,
      scac: null,
      smallPackageCarrier: false,
      activeStatus: true,
    });
  }

  isInvalid(formControlName: string) {
    return !this.form.controls[formControlName].pristine && this.form.controls[formControlName].invalid;
  }
}
